"use client";

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getArtists } from "@/features/artists/api";
import { inviteToGallery } from "@/features/galleries/api";
import { useDebounce } from "@/hooks/useDebounce";
import { toastApiError } from "@/lib/api-toast";

/* eslint-disable @typescript-eslint/no-explicit-any -- the artists search payload is untyped, as it was in GalleryInviteModal */

export type InviteCandidate = {
  id: number;
  name?: string;
  username?: string;
  profile?: {
    cover?: string | null;
  } | null;
};

/**
 * Inviting a user to a gallery — GalleryInviteModal's flow, moved here
 * unchanged so the legacy dialog and the v2 one run the same thing:
 *  - everything resets when the dialog closes;
 *  - the search is `/artists?name=` (10), debounced 400ms, a stale answer dropped;
 *  - typing clears the pick, the sent state and the error;
 *  - inviting needs a picked user; on failure the pick and the search stay,
 *    so a transient error does not cost the owner the search.
 */
export function useGalleryInvite({
  open,
  galleryId,
  isFa
}: {
  open: boolean;
  galleryId: number;
  isFa: boolean;
}) {
  const [search, setSearchValue] = useState("");
  const [results, setResults] = useState<InviteCandidate[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<InviteCandidate | null>(null);
  const [isInviting, setIsInviting] = useState(false);
  // The in-flight latch: a double press sends one invitation.
  const inFlight = useRef(false);
  const [invited, setInvited] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search, 400);
  useEffect(() => {
    if (!open) {
      setSearchValue("");
      setResults([]);
      setSelectedUser(null);
      setInvited(false);
      setError(null);
    }
  }, [open]);
  useEffect(() => {
    if (!open || !debouncedSearch.trim()) {
      setResults([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsSearching(true);
      try {
        const res = await getArtists({
          name: debouncedSearch.trim(),
          perPage: 10,
          page: 1
        });
        const artists = (res as any)?.data?.artists ?? [];
        if (!cancelled) setResults(Array.isArray(artists) ? artists : []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, open]);
  const setSearch = (value: string) => {
    setSearchValue(value);
    setSelectedUser(null);
    setInvited(false);
    setError(null);
  };
  const pick = (candidate: InviteCandidate) => {
    setSelectedUser(candidate);
    setError(null);
  };
  const invite = async () => {
    if (isInviting || inFlight.current) return;
    if (!selectedUser?.id) {
      setError(isFa ? "ابتدا یک کاربر را انتخاب کنید." : "Select a user first.");
      return;
    }
    setError(null);
    inFlight.current = true;
    setIsInviting(true);
    try {
      await inviteToGallery(galleryId, Number(selectedUser.id));
      // The invitation reaches the invitee as a notification with its own deep
      // link — the owner never sees or shares the link, so we only confirm.
      setInvited(true);
      toast.success(isFa ? "دعوت‌نامه ارسال شد" : "Invitation sent");
    } catch (err) {
      // Selection and search text are preserved so a recoverable failure (e.g.
      // a transient network error) doesn't force the owner to search again.
      toastApiError(err, isFa ? "خطا در ارسال دعوت" : "Failed to send invitation");
    } finally {
      inFlight.current = false;
      setIsInviting(false);
    }
  };
  return {
    search,
    setSearch,
    debouncedSearch,
    results,
    isSearching,
    selectedUser,
    pick,
    invite,
    isInviting,
    invited,
    error
  };
}